import React, { useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { Paper, PaperBorderType, CurrencyField, StandardDialog, Button, TextField } from '@roc/ui';
import { observer } from 'mobx-react';
import { PayoffReconciliationFieldsCard } from './payoffReconciliationFieldsCard';
import { PayoffReconciliationFinalCard } from './payoffReconciliationFinalCard';
import { Block, Category, CheckCircle, CloudCircle, Edit } from '@material-ui/icons';
import PayoffReconciliationCommentDialog from './payoffReconciliationCommentDialog';
import { PayoffReconciliationReviewRequest } from './payoffReconciliationReviewRequest';
import { useDocumentStore } from 'libs/feature-documents/src/documents/hooks/useDocumentStore';
import { PayoffReconciliationReviewCategory } from './payoffReconciliationReviewCategory';


const useStyles = makeStyles((theme: Theme) => ({
  table: {
    minWidth: 750,
    border: '2px #e0e0e0 solid',
    '& td': {
      height: '40px',
      padding: 0,
      paddingTop: '8px',
      paddingBottom: '8px',
      paddingLeft: theme.spacing(2),
      paddingRight: '3px',
    },
    '& th:first-child, td[rowSpan], $addNewItemRow td': {
      paddingLeft: theme.spacing(2),
      paddingRight: '3px',
    },
    '& table tr:last-child td, & table td[rowSpan]': {
      borderBottom: 'none',
    },
  },
  paper: {
    padding: theme.spacing(3, 0, 4),
    boxShadow: 'none',
  },
  headerGrid: {
    padding: '12px 24px 12px 24px',
  },
  button: {
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  finalCard: {
    display: 'flex',
    justifyContent: 'right',
    width: '100%',
  }
}));

export const PayoffReconciliationReviewCategoryTable = observer(({ allowPayoffWaiverReview }) => {
  const classes = useStyles();
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [record, setRecord] = useState(null);

  let comment = '';

  const {
    documentStore: {
      documentFormStore
    },
  } = useDocumentStore();
  const { payoffReconciliationReviewStore } = documentFormStore;

  useEffect(() => {
    payoffReconciliationReviewStore.initialize();
  }, []);

  const handleOpenModal = (item) => {
    setRecord(item);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setRecord(null);
    setOpenModal(false);
  };

  const updateWaiverItem = async () => {
    await payoffReconciliationReviewStore.updateWaiverItem(record);
    handleCloseModal();
  };

  const approveWaiverItem = async (comment) => {
    await payoffReconciliationReviewStore.approveWaiverItem(record);
    setIsApproveDialogOpen(false);
  }

  const rejectWaiverItem = async (comment) => {
    await payoffReconciliationReviewStore.rejectWaiverItem(record, comment);
    setIsRejectDialogOpen(false);
  }

  const submitWaiverRequest = async () => {
    await payoffReconciliationReviewStore.submitWaiverRequest();
    documentFormStore.closeCurrentForm();
  }

  const markRequestAsResolved = async () => {
    await payoffReconciliationReviewStore.markRequestAsResolved();
    documentFormStore.closeCurrentForm();
  }

  return (
    <Grid item xs={12} md={12}>
    <Paper className={classes.paper} borderType={PaperBorderType.NO_BORDER}>
      <Grid item xs={12}>
        <PayoffReconciliationFieldsCard store={payoffReconciliationReviewStore} />
      </Grid>
    </Paper>
    <Paper className={classes.paper} borderType={PaperBorderType.NO_BORDER}>
      <Grid container spacing={2} className={classes.headerGrid}>
        <PayoffReconciliationReviewCategory
          store={payoffReconciliationReviewStore}
          categoryTitle={'Interest'}
          categoryContents={payoffReconciliationReviewStore?.interestWaiverItems}
          allowPayoffWaiverReview={allowPayoffWaiverReview}
          setIsApproveDialogOpen={setIsApproveDialogOpen}
          setIsRejectDialogOpen={setIsRejectDialogOpen}
          handleOpenModal={handleOpenModal}
          setRecord={setRecord}
        />
        <PayoffReconciliationReviewCategory
          store={payoffReconciliationReviewStore}
          categoryTitle={'Servicer Fees'}
          categoryContents={payoffReconciliationReviewStore?.servicerFeeWaiverItems}
          allowPayoffWaiverReview={allowPayoffWaiverReview}
          setIsApproveDialogOpen={setIsApproveDialogOpen}
          setIsRejectDialogOpen={setIsRejectDialogOpen}
          handleOpenModal={handleOpenModal}
          setRecord={setRecord}
        />
        <PayoffReconciliationReviewCategory
          store={payoffReconciliationReviewStore}
          categoryTitle={'Unpaid Charges'}
          categoryContents={payoffReconciliationReviewStore?.unpaidChargeWaiverItems}
          allowPayoffWaiverReview={allowPayoffWaiverReview}
          setIsApproveDialogOpen={setIsApproveDialogOpen}
          setIsRejectDialogOpen={setIsRejectDialogOpen}
          handleOpenModal={handleOpenModal}
          setRecord={setRecord}
        />
        </Grid>
      </Paper>
      <Paper className={classes.paper} borderType={PaperBorderType.NO_BORDER}>
        <Grid item xs={12} md={12} className={classes.finalCard}>
          <PayoffReconciliationFinalCard store={payoffReconciliationReviewStore} />
        </Grid>
      </Paper>
      <Paper className={classes.paper} borderType={PaperBorderType.NO_BORDER}>
        <Grid item className={classes.button}>
          {!allowPayoffWaiverReview && !payoffReconciliationReviewStore.isRequestResolved && (
            <Box display={'flex'} justifyContent={'center'} width={'35%'}>
              <Button
                testId='saveRequest'
                variant='contained'
                color='primary'
                size='large'
                fullWidth
                onClick={submitWaiverRequest}
              >Submit
              </Button>
            </Box>
          )}
          {allowPayoffWaiverReview && !payoffReconciliationReviewStore.isRequestResolved && (
            <Box display={'flex'} justifyContent={'center'} width={'35%'}>
              <Button
                testId='resolveRequest'
                variant='outlined'
                color='primary'
                size='large'
                fullWidth
                onClick={markRequestAsResolved}
              >Mark as Resolved
              </Button>
            </Box>
          )}
        </Grid>
      </Paper>
      <StandardDialog
        open={isApproveDialogOpen}
        title="Approve Payoff Request"
        maxWidth="sm"
        handleClose={() => setIsApproveDialogOpen(false)}
        dialogContent={
          <PayoffReconciliationCommentDialog
            onClose={() => setIsApproveDialogOpen(false)}
            subTitle={"You're approving this request payoff. The client will be notified."}
            input={comment}
            onSubmit={approveWaiverItem}
            includeCommentBox={false}
          />
        }
      />
      <StandardDialog
        open={isRejectDialogOpen}
        title="Deny Payoff Request"
        maxWidth="sm"
        handleClose={() => setIsRejectDialogOpen(false)}
        dialogContent={
          <PayoffReconciliationCommentDialog
            onClose={() => setIsRejectDialogOpen(false)}
            subTitle={"You're denying the entire payoff. The client will be notified."}
            input={comment}
            onSubmit={rejectWaiverItem}
          />
        }
      />
      <Dialog maxWidth='sm' fullWidth open={openModal} onClose={handleCloseModal}>
        <DialogTitle>Request Waiver for {record?.name}</DialogTitle>
        <DialogContent>
          <PayoffReconciliationReviewRequest record={record} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseModal}
            color="primary"
            testId="cancel"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={updateWaiverItem}
            color="primary"
            testId="submit"
            variant="contained"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
});
